/* The employee chat — the prototype's hero screen, wired to the real API.
 *
 * Layout, class names and copy follow kbl-internal-ai-assistant_06_updated.html:
 * collapsible conversation rail (.chatside), single scroll container (.chat-scroll),
 * starter chips above the composer, word-streamed answers, cite pills, and the
 * Helpful / Not helpful / Copy action row.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'

import { chat } from '../api/client'
import type {
  Citation,
  FeedbackRating,
  HighlightSpan,
  Me,
  Message,
  SessionSummary,
  Welcome,
} from '../api/types'
import { AnswerBlocks, Inline, parseAnswer } from '../ui/AnswerView'
import type { AnswerBlock } from '../ui/answerFormat'
import { ago, errorMessage, useConfirm, useModal, useToast } from '../ui/chrome'
import { Icon } from '../ui/icons'
import type { IconName } from '../ui/icons'
import { registerConversationExport } from '../ui/pdf'

const MAX_QUESTION = 600

/** The four things employees arrive wanting to do. Each is a real question, not a label —
 *  these used to be static text that read as links and did nothing when clicked. */
const TASKS: { icon: IconName; label: string; prompt: string }[] = [
  { icon: 'doc', label: 'Claim medical reimbursement', prompt: 'How do I claim medical reimbursement?' },
  { icon: 'cal', label: 'Apply for leave', prompt: 'How do I apply for leave?' },
  { icon: 'monitor', label: 'Raise an IT request', prompt: 'How do I raise an IT request?' },
  { icon: 'users', label: 'New employee onboarding', prompt: 'What is the onboarding process for a new employee?' },
]

/**
 * The spans to highlight inside a citation's excerpt.
 *
 * Prefers the full list and falls back to the single start/end pair, so a response from an
 * older server — or a citation stored before span verification existed — still renders. An
 * empty result means "we could not pinpoint the supporting text", which is rendered as no
 * highlight at all. Highlighting the whole excerpt is never a fallback: the excerpt is the
 * passage the answer was drawn FROM, not the sentence that supports it, and marking all of it
 * tells the reader something we know to be untrue.
 */
function spansOf(citation: Citation): HighlightSpan[] {
  if (citation.highlight_spans?.length) return citation.highlight_spans
  if (citation.highlight_start != null && citation.highlight_end != null) {
    return [{ start: citation.highlight_start, end: citation.highlight_end }]
  }
  return []
}

/**
 * Renders `text` with `spans` marked.
 *
 * Built from string slices rendered as JSX children, never as an HTML string — React escapes
 * text nodes, so document content containing `<`, `&` or quotes cannot become markup. The
 * original characters are preserved byte for byte, including newlines and non-ASCII, because
 * the text is never rewritten, only cut.
 *
 * Offsets arrive from the server but are re-validated and re-merged here anyway: this
 * component must not produce overlapping <mark> elements or slice out of bounds whatever it
 * is handed.
 */
function HighlightedExcerpt({ text, spans }: { text: string; spans: HighlightSpan[] }) {
  const merged: HighlightSpan[] = []
  const usable = spans
    .filter(
      (s) =>
        Number.isInteger(s.start) &&
        Number.isInteger(s.end) &&
        s.start >= 0 &&
        s.end > s.start &&
        s.start < text.length,
    )
    .map((s) => ({ start: s.start, end: Math.min(s.end, text.length) }))
    .sort((a, b) => a.start - b.start)

  for (const span of usable) {
    const last = merged[merged.length - 1]
    if (last && span.start <= last.end) last.end = Math.max(last.end, span.end)
    else merged.push({ ...span })
  }

  if (merged.length === 0) return <>{text}</>

  const parts: ReactNode[] = []
  let cursor = 0
  merged.forEach((span, index) => {
    if (span.start > cursor) parts.push(text.slice(cursor, span.start))
    parts.push(<mark key={`hl-${index}`}>{text.slice(span.start, span.end)}</mark>)
    cursor = span.end
  })
  if (cursor < text.length) parts.push(text.slice(cursor))
  return <>{parts}</>
}

/** Word-by-word reveal, ~3 words per 16-36ms tick, exactly like the prototype.
 *  Whitespace runs are kept as their own tokens and re-joined verbatim, so the newlines
 *  that separate an answer's bullets survive the reveal instead of collapsing into one
 *  run-on line and then snapping into shape when streaming ends. */
function StreamingText({ text, onDone }: { text: string; onDone: () => void }) {
  const [count, setCount] = useState(0)
  const words = useMemo(
    () => text.replace(/\*\*/g, '').split(/(\s+)/).filter(Boolean),
    [text],
  )
  const doneRef = useRef(false)

  useEffect(() => {
    let cancelled = false
    let timer: ReturnType<typeof setTimeout>
    function tick() {
      if (cancelled) return
      setCount((c) => {
        if (c >= words.length) return c
        return c + 6 // ~3 words: each word carries a whitespace token with it
      })
      timer = setTimeout(tick, 16 + Math.random() * 20)
    }
    tick()
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [words.length])

  useEffect(() => {
    if (count >= words.length && !doneRef.current) {
      doneRef.current = true
      onDone()
    }
  }, [count, words.length, onDone])

  return <p className="streaming">{words.slice(0, count).join('')}</p>
}

/* -------------------------------------------------------------- edge copy */

function edgeParas(message: Message): string[] | null {
  switch (message.answer_state) {
    case 'no_kb_match':
      // Short on purpose. The first paragraph becomes the 29px headline, so it carries the
      // whole message on its own for anyone who reads no further. "Indexed" was jargon, and
      // the category list restated the coverage note the empty state already gives. The scope
      // hint and the "Report a gap" action are rendered separately below — repeating them
      // here made a dead end read like a wall of instructions.
      return [
        "I couldn't find an answer to this.",
        'It may not be covered in the KBL documents I can search. Try rewording your question, or contact HR or the IT Helpdesk.',
      ]
    case 'access_filtered':
      // SECURITY: never name the restricted documents, quote them, or reveal how many matched.
      return [
        'Documents matching this question exist, but they are outside your access permissions.',
        'Document access follows your Azure AD groups. If you believe you should have access, contact IT Helpdesk.',
      ]
    case 'out_of_scope':
      return [
        'This is outside the scope of the assistant.',
        message.content ||
          'The assistant answers questions about KBL policies, manuals and circulars, and points you to the right form or portal.',
      ]
    case 'unavailable':
      return [
        'The assistant is temporarily unavailable.',
        message.content ||
          'The answering service is not reachable in this environment, so questions cannot be answered yet.',
        'This is a configuration issue on our side. Try again later, or contact IT Helpdesk if it persists.',
      ]
    default:
      return null
  }
}

/**
 * The Option B answer sheet leads with the answer itself as a 29px headline. LLM answers are
 * free prose, so the headline is carved deterministically: the first paragraph if it is short
 * enough, otherwise its first sentence; a list-first answer gets no headline at all. Nothing
 * is rewritten — the text is only split, so headline + body always reads as the full answer.
 */
function splitHeadline(blocks: AnswerBlock[]): { headline: string | null; rest: AnswerBlock[] } {
  const first = blocks[0]
  if (!first || first.kind !== 'p') return { headline: null, rest: blocks }
  const text = first.text
  if (text.length <= 200) return { headline: text, rest: blocks.slice(1) }
  const sentence = /^(.{20,200}?[.!?])\s+(.+)$/.exec(text)
  const head = sentence?.[1]
  const tail = sentence?.[2]
  if (!head || !tail) return { headline: null, rest: blocks }
  return {
    headline: head,
    rest: [{ kind: 'p', text: tail }, ...blocks.slice(1)],
  }
}

/** The unscoped default — every category this user can see. */
const ALL_SCOPES = 'All I can access'

/** The Option B "Report a gap" reasons, verbatim. */
const GAP_REASONS = [
  'Answer is out of date',
  'Wrong document cited',
  'Policy is missing',
  'Answer is unclear',
] as const

/** Modal body for "Report a gap" — owns its own selection state so the modal provider can
 *  stay dumb. Submits reason + free text; the caller wires it to the feedback API. */
function GapReportBody({
  onSend,
  onCancel,
}: {
  onSend: (reason: string, detail: string) => void
  onCancel: () => void
}) {
  const [reason, setReason] = useState<string>(GAP_REASONS[0])
  const [detail, setDetail] = useState('')
  return (
    <div className="gap-body">
      <div className="gap-sec">
        <span className="ce-eyebrow">What went wrong</span>
        <div className="gap-reasons">
          {GAP_REASONS.map((r) => (
            <button
              key={r}
              type="button"
              className={`scopechip${reason === r ? ' on' : ''}`}
              onClick={() => setReason(r)}
            >
              {r}
            </button>
          ))}
        </div>
      </div>
      <div className="gap-sec">
        <span className="ce-eyebrow">Add detail (optional)</span>
        <textarea
          className="inp"
          rows={3}
          value={detail}
          placeholder="e.g. A newer circular supersedes this policy…"
          onChange={(event) => setDetail(event.target.value)}
        />
      </div>
      <div className="gap-attached">
        <b>Attached automatically</b>
        <span>Your question, the answer shown, and the cited documents with page numbers.</span>
      </div>
      <div className="gap-foot">
        <button type="button" className="btn btn-soft" onClick={onCancel}>
          Cancel
        </button>
        <button type="button" className="btn" onClick={() => onSend(reason, detail.trim())}>
          Send report
        </button>
      </div>
    </div>
  )
}


/* ------------------------------------------------------------------ screen */

export default function ChatScreen({ me }: { me: Me }) {
  const toast = useToast()
  const confirm = useConfirm()
  const { openModal, closeModal } = useModal()

  const [welcome, setWelcome] = useState<Welcome | null>(null)
  const [sessions, setSessions] = useState<SessionSummary[] | null>(null)
  const [sessionId, setSessionId] = useState<string | null>(null)
  const [sessionTitle, setSessionTitle] = useState('New conversation')
  const [messages, setMessages] = useState<Message[]>([])
  const [draft, setDraft] = useState('')
  const [search, setSearch] = useState('')
  const [busy, setBusy] = useState(false)
  const [sideOpen, setSideOpen] = useState(false)
  const [streamId, setStreamId] = useState<string | null>(null)
  const [stick, setStick] = useState(true)
  const [jumpVisible, setJumpVisible] = useState(false)
  /** Which answer's sources fill the right-hand panel; null means "the latest answer". */
  const [activeSrcId, setActiveSrcId] = useState<string | null>(null)
  /** Source card being flash-highlighted after its chip was clicked. */
  const [flashKey, setFlashKey] = useState<string | null>(null)
  /** Below 1240px the panel is an overlay; a chip click opens it, its × closes it. */
  const [srcOverlay, setSrcOverlay] = useState(false)
  /** The selected scope — a full category name, or ALL_SCOPES for everything. Sent with
   *  every ask, so answers come only from that shelf. */
  const [scope, setScope] = useState(ALL_SCOPES)

  const storeKey = `kbl.chat.session.${me.object_id}`
  /* Read synchronously on the first render, not in an effect: if a conversation is about to
   * be restored, the "what would you like to know?" hero must never paint first and then be
   * replaced by the transcript a frame later. */
  const [resuming, setResuming] = useState(() => {
    try {
      return Boolean(sessionStorage.getItem(`kbl.chat.session.${me.object_id}`))
    } catch {
      return false
    }
  })

  const scrollRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  /** Which scope each assistant message was asked under, keyed by message id — so an
   *  unanswered scoped question can say WHY nothing was found. */
  const askScopes = useRef<Record<string, string>>({})
  /** Follow-up questions per assistant message id, from the ask response. Transient. */
  const followups = useRef<Record<string, string[]>>({})
  /** Each scope keeps its own conversation: leaving a scope parks its session here, and
   *  returning to the scope resumes it. Survives navigation via sessionStorage. */
  const scopeSessions = useRef<Record<string, string>>({})
  const scopeStoreKey = `kbl.chat.scope-sessions.${me.object_id}`
  useEffect(() => {
    try {
      scopeSessions.current = JSON.parse(sessionStorage.getItem(scopeStoreKey) ?? '{}')
    } catch {
      scopeSessions.current = {}
    }
  }, [scopeStoreKey])

  const persistScopeSessions = useCallback(() => {
    try {
      sessionStorage.setItem(scopeStoreKey, JSON.stringify(scopeSessions.current))
    } catch {
      /* storage blocked — resume is a convenience */
    }
  }, [scopeStoreKey])

  const refreshSessions = useCallback(async (term: string) => {
    try {
      setSessions(await chat.listSessions({ q: term || undefined, limit: 100 }))
    } catch {
      setSessions([])
    }
  }, [])

  useEffect(() => {
    chat
      .welcome()
      .then(setWelcome)
      .catch(() => setWelcome(null))
  }, [])

  useEffect(() => {
    const timer = setTimeout(() => void refreshSessions(search), search ? 250 : 0)
    return () => clearTimeout(timer)
  }, [search, refreshSessions])

  /* ------------------------------------------------------ resume the conversation
   * Visiting Dashboard or any other screen unmounts this component, so the open
   * conversation lived only as long as the screen did — coming back always landed on a blank
   * chat, with the previous exchange only reachable by hunting for it in the rail.
   *
   * The id is remembered per signed-in user, in sessionStorage rather than localStorage: it
   * should survive navigation and a reload of the tab, not reopen a days-old conversation on
   * a fresh browser start. Keyed by object_id so switching identity locally cannot resurrect
   * somebody else's chat.
   *
   * Only the id is stored. The transcript is re-read from the server, so a conversation
   * continued in another tab comes back current rather than stale. */
  const hydrated = useRef(false)

  useEffect(() => {
    let saved: string | null = null
    try {
      saved = sessionStorage.getItem(storeKey)
    } catch {
      /* storage blocked — resume is a convenience, not a requirement */
    }
    if (!saved) {
      hydrated.current = true
      setResuming(false)
      return
    }
    let cancelled = false
    chat
      .getSession(saved)
      .then((detail) => {
        if (cancelled) return
        setSessionId(detail.id)
        setSessionTitle(detail.title ?? 'Conversation')
        setMessages(detail.messages)
        setStick(true)
      })
      .catch(() => {
        // Deleted, archived, or no longer readable — start clean, without an error the
        // user cannot act on.
        try {
          sessionStorage.removeItem(storeKey)
        } catch {
          /* ignore */
        }
      })
      .finally(() => {
        hydrated.current = true
        if (!cancelled) setResuming(false)
      })
    return () => {
      cancelled = true
    }
  }, [storeKey])

  // Guarded on `hydrated` so the mount-time null does not wipe the key before it is read.
  useEffect(() => {
    if (!hydrated.current) return
    try {
      if (sessionId) sessionStorage.setItem(storeKey, sessionId)
      else sessionStorage.removeItem(storeKey)
    } catch {
      /* ignore */
    }
  }, [sessionId, storeKey])

  /* Register the transcript with the shell's "Export conversation" action. */
  useEffect(() => {
    registerConversationExport(() => {
      if (messages.length === 0) return null
      return {
        title: sessionTitle,
        blocks: messages.map((m) => ({
          h: `${m.role === 'user' ? me.display_name : 'KBL Internal AI Assistant'}  -  ${new Date(m.created_at).toLocaleString('en-IN')}`,
          t:
            m.content.replace(/\*\*/g, '') +
            (m.citations.length > 0
              ? '\n\nSources: ' + m.citations.map((c) => `${c.document_name} p.${c.page_number}`).join('  |  ')
              : ''),
        })),
      }
    })
    return () => registerConversationExport(null)
  }, [messages, sessionTitle, me.display_name])

  /* Stick-to-bottom bookkeeping — .chat-scroll is the only scroll container. */
  const updateJump = useCallback(() => {
    const el = scrollRef.current
    if (!el) return
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 64
    setStick(nearBottom)
    setJumpVisible(el.scrollHeight - el.clientHeight > 40 && !nearBottom)
  }, [])

  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    // Stick to the newest answer — but never scroll the home screen, which has no "latest".
    if (stick && messages.length > 0) el.scrollTop = el.scrollHeight
  })

  function scrollToBottom() {
    const el = scrollRef.current
    if (!el) return
    el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' })
    setStick(true)
    setJumpVisible(false)
  }

  /* ------------------------------------------------------------- sessions */

  async function openSession(id: string) {
    try {
      const detail = await chat.getSession(id)
      setSessionId(detail.id)
      setSessionTitle(detail.title ?? 'Conversation')
      setMessages(detail.messages)
      setStreamId(null)
      setStick(true)
    } catch (cause) {
      toast('err', 'Could not open that conversation', errorMessage(cause))
    }
  }

  function startNewChat() {
    if (messages.length === 0 && !sessionId) {
      toast('info', 'Already a new conversation', 'Ask something to start it.')
      return
    }
    setSessionId(null)
    setSessionTitle('New conversation')
    setMessages([])
    setStreamId(null)
    setSearch('')
    // The abandoned conversation must not resurface when this scope is re-selected.
    delete scopeSessions.current[scope]
    persistScopeSessions()
    toast('info', 'New conversation started', 'Context from the previous conversation is not carried over.')
  }

  async function deleteSession(id: string) {
    try {
      await chat.archiveSession(id)
      if (id === sessionId) {
        setSessionId(null)
        setSessionTitle('New conversation')
        setMessages([])
      }
      void refreshSessions(search)
      toast('ok', 'Conversation deleted')
    } catch (cause) {
      toast('err', 'Delete failed', errorMessage(cause))
    }
  }

  function clearHistory() {
    const all = sessions ?? []
    if (all.length === 0) return
    confirm(
      'Clear all conversations?',
      'Every conversation in your history will be permanently deleted.',
      'Clear history',
      () => {
        void (async () => {
          for (const s of all) {
            try {
              await chat.archiveSession(s.id)
            } catch {
              /* keep going — one failure should not strand the rest */
            }
          }
          setSessionId(null)
          setSessionTitle('New conversation')
          setMessages([])
          void refreshSessions('')
          toast('ok', 'History cleared')
        })()
      },
      true,
    )
  }

  /* ----------------------------------------------------------------- send */

  /** `scopeOverride` (null = search everything) beats the chip state for this one ask —
   *  needed because React state set in the same tick would not reach this closure. */
  async function send(question: string, scopeOverride?: string | null) {
    const q = question.trim()
    if (busy) return
    if (!q) {
      toast('warn', 'Type a question first')
      inputRef.current?.focus()
      return
    }
    if (q.length > MAX_QUESTION) {
      toast('err', 'Question too long', `Keep it under ${MAX_QUESTION} characters.`)
      return
    }

    setDraft('')
    setBusy(true)
    setStick(true)

    const optimistic: Message = {
      id: `pending-${Date.now()}`,
      role: 'user',
      content: q,
      created_at: new Date().toISOString(),
      citations: [],
      is_answered: null,
      answer_state: null,
      confidence: null,
      feedback: null,
    }
    setMessages((current) => [...current, optimistic])

    try {
      const scopeUsed =
        scopeOverride !== undefined ? scopeOverride : scope !== ALL_SCOPES ? scope : null
      const response = await chat.ask({
        message: q,
        session_id: sessionId,
        scope: scopeUsed ?? undefined,
      })
      if (scopeUsed) askScopes.current[response.message_id] = scopeUsed
      followups.current[response.message_id] = response.followups ?? []
      // This conversation now belongs to the scope it was asked under.
      scopeSessions.current[scopeUsed ?? ALL_SCOPES] = response.session_id
      persistScopeSessions()
      // Re-read rather than appending locally: the server owns message ids, and feedback
      // needs the real one.
      const detail = await chat.getSession(response.session_id)
      setSessionId(detail.id)
      setSessionTitle(detail.title ?? (q.length > 42 ? `${q.slice(0, 42)}…` : q))
      setMessages(detail.messages)
      const last = detail.messages[detail.messages.length - 1]
      if (last && last.role === 'assistant') setStreamId(last.id)
      void refreshSessions(search)
    } catch (cause) {
      setMessages((current) => current.filter((m) => m.id !== optimistic.id))
      setDraft(q) // hand the question back rather than losing it
      toast('err', 'The assistant could not answer', errorMessage(cause))
    } finally {
      setBusy(false)
    }
  }

  /* Helpful / Not helpful — restored from the prototype's action row. The rating is stored
   * against the real message id and reflected back into local state so the buttons read the
   * recorded state, not a hope. Clicking the same rating again is a no-op (the API has no
   * "unset"). */
  async function sendFeedback(message: Message, rating: FeedbackRating) {
    if (message.feedback === rating) return
    try {
      await chat.sendFeedback(message.id, { rating })
      setMessages((current) =>
        current.map((m) => (m.id === message.id ? { ...m, feedback: rating } : m)),
      )
      if (rating === 'down') {
        toast('info', 'Flagged for review', 'This answer will be checked by the knowledge owner.')
      } else {
        toast('ok', 'Feedback recorded')
      }
    } catch (cause) {
      toast('err', 'Feedback failed', errorMessage(cause))
    }
  }

  function copyMessage(message: Message) {
    let text = message.content.replace(/\*\*/g, '')
    if (message.citations.length > 0) {
      text +=
        '\n\nSources:\n' +
        message.citations
          .map((c) => `${c.rank}. ${c.document_name}, p.${c.page_number}`)
          .join('\n')
    }
    navigator.clipboard
      .writeText(text)
      .then(() => toast('ok', 'Answer copied'))
      .catch(() => toast('err', 'Copy failed'))
  }

  /* ------------------------------------------------------------ citations */

  /* The sources column stays open beside the answer (Option B). A chip click retargets the
   * panel at that answer's citations, scrolls the matching card into view and flashes it. */
  function showSource(message: Message, rank: number) {
    setActiveSrcId(message.id)
    setSrcOverlay(true)
    const key = `${message.id}-${rank}`
    setFlashKey(key)
    window.setTimeout(() => {
      document.getElementById(`src-${key}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }, 80)
    window.setTimeout(() => setFlashKey((current) => (current === key ? null : current)), 1700)
  }

  /* ---------------------------------------------------------- report a gap */

  async function submitGap(message: Message, reason: string, detail: string) {
    try {
      await chat.sendFeedback(message.id, {
        rating: 'down',
        comment: detail ? `[${reason}] ${detail}` : `[${reason}]`,
      })
      setMessages((current) =>
        current.map((m) => (m.id === message.id ? { ...m, feedback: 'down' } : m)),
      )
      toast('ok', 'Gap report sent', 'The knowledge owner sees your question, the answer and its sources.')
    } catch (cause) {
      toast('err', 'Could not send the report', errorMessage(cause))
    }
  }

  function openGap(message: Message | null) {
    if (!message) return
    openModal({
      title: 'Report a gap',
      sub: 'Tell us what was wrong or missing. This goes to the knowledge owner, not a support queue.',
      size: 'narrow',
      body: (
        <GapReportBody
          onCancel={closeModal}
          onSend={(reason, detail) => {
            closeModal()
            void submitGap(message, reason, detail)
          }}
        />
      ),
      footer: false,
    })
  }

  /* -------------------------------------------------------------- derived */

  /* Entry-screen suggestions, grouped by category. The API sends several prompts per
   * category, so labelling every row would print "HR POLICIES" three times running. Sorting
   * by category and labelling only the first of each run makes the column a grouping rather
   * than a repeated tag. */
  const suggestions = useMemo(() => {
    const inScope = (welcome?.suggested_prompts ?? []).filter(
      (p) => scope === ALL_SCOPES || p.category === scope,
    )
    // Slice FIRST, then group. The server interleaves categories deliberately so the unscoped
    // strip draws from several areas; sorting the whole list alphabetically before taking five
    // undid that and handed back four prompts from whichever category sorts first. Sorting the
    // five that survive keeps same-category rows adjacent for the label logic below without
    // changing which five they are.
    const sorted = inScope.slice(0, 5).sort((a, b) => a.category.localeCompare(b.category))
    return sorted.map((p, i) => ({
      ...p,
      showCategory: i === 0 || p.category !== sorted[i - 1]?.category,
    }))
  }, [welcome, scope])

  /* Scope chips come straight from the categories this user's groups can reach — the server
   * decides the list, and the same server validates whichever one is sent back with an ask.
   * The chip label is the category's first word ("HR Policies" → "HR") unless that would
   * collide with another category, in which case the full name is shown. */
  const scopes = useMemo(() => {
    const cats = welcome?.accessible_categories ?? []
    const firsts = cats.map((c) => c.trim().split(/\s+/)[0] ?? c)
    return [
      { value: ALL_SCOPES, label: ALL_SCOPES },
      ...cats.map((c, i) => {
        const short = firsts[i] ?? c
        const collides = firsts.filter((f) => f === short).length > 1
        return { value: c, label: collides ? c : short }
      }),
    ]
  }, [welcome])

  /* "New chat" on the button and nothing more — it is pressed often and read once, so the
   * label should be a name rather than a sentence. The scope lives in the tooltip instead,
   * where it answers "what will this give me" for anyone who pauses, without making the
   * control itself longer every time a scope is picked. `isFreshChat` disables it: a button
   * that reports "you are already here" when pressed should not have invited the press. */
  const scopeShort = useMemo(
    () => scopes.find((s) => s.value === scope)?.label ?? scope,
    [scopes, scope],
  )
  const isFreshChat = messages.length === 0 && !sessionId
  const newChatHint = isFreshChat
    ? 'This is already a fresh conversation'
    : scope === ALL_SCOPES
      ? 'Start a new chat'
      : `Start a new chat in ${scopeShort}`

  /* BROWSE BY AREA — the same categories as the chips. Picking one selects its scope and
   * focuses the ask box; picking it again clears back to everything. */
  const areas = useMemo(() => {
    const prompts = welcome?.suggested_prompts ?? []
    return (welcome?.accessible_categories ?? []).map((name) => {
      const count = prompts.filter((p) => p.category === name).length
      return {
        name,
        hint: count > 0 ? `${count} common question${count === 1 ? '' : 's'}` : 'Browse this area',
      }
    })
  }, [welcome])

  /** Clear to an empty conversation without the toasts `startNewChat` raises. */
  function freshChat() {
    setSessionId(null)
    setSessionTitle('New conversation')
    setMessages([])
    setStreamId(null)
  }

  /** Scope change = conversation change. The current scope's session is parked, and the
   *  destination scope either resumes its own earlier conversation or starts clean. */
  async function selectScope(next: string) {
    if (next === scope || busy) return
    if (sessionId) {
      scopeSessions.current[scope] = sessionId
      persistScopeSessions()
    }
    setScope(next)
    const prior = scopeSessions.current[next]
    if (!prior) {
      freshChat()
      return
    }
    try {
      const detail = await chat.getSession(prior)
      setSessionId(detail.id)
      setSessionTitle(detail.title ?? 'Conversation')
      setMessages(detail.messages)
      setStreamId(null)
      setStick(true)
    } catch {
      // Deleted or unreadable since it was parked — forget it and start clean.
      delete scopeSessions.current[next]
      persistScopeSessions()
      freshChat()
    }
  }

  function browseArea(name: string) {
    void selectScope(scope === name ? ALL_SCOPES : name)
    inputRef.current?.focus()
  }

  /* The answer whose sources fill the right-hand column: the one a chip targeted, or the
   * latest cited answer. The panel renders only when this exists. */
  const srcMessage = useMemo(() => {
    const targeted = messages.find((m) => m.id === activeSrcId)
    if (targeted && targeted.citations.length > 0) return targeted
    return [...messages].reverse().find((m) => m.role === 'assistant' && m.citations.length > 0) ?? null
  }, [messages, activeSrcId])

  /** Nothing asked yet — the screen is an invitation rather than a transcript. `resuming`
   *  holds it back while a saved conversation is being re-read. */
  const atStart = messages.length === 0 && !busy && !resuming

  const filteredSessions = sessions ?? []

  /* --------------------------------------------------------------- render */

  const greet = (() => {
    const hour = new Date().getHours()
    if (hour < 12) return 'Good morning'
    if (hour < 17) return 'Good afternoon'
    return 'Good evening'
  })()

  const kbDown = welcome?.knowledge_base_available === false
  const lastAssistant =
    [...messages].reverse().find((m) => m.role === 'assistant' && m.id !== streamId) ?? null

  return (
    <div className={`chatwrap${sideOpen ? ' side-on' : ''}`}>
      {/* Conversation history — collapsible for everyone, summoned by the edge tab. */}
      <aside className="chatside" id="chatSide">
        <div className="chatside-h">
          <button
            type="button"
            className="btn btn-lime btn-block"
            onClick={startNewChat}
            disabled={isFreshChat}
            title={newChatHint}
          >
            <Icon name="plus" size={16} /> New chat
          </button>
          <div className="chatsearch">
            <Icon name="search" size={14} />
            <input
              id="histQ"
              placeholder="Search conversations"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </div>
        </div>
        <div className="chatlist" id="chatList">
          {sessions === null && (
            <div className="tiny muted" style={{ padding: '18px 12px', textAlign: 'center' }}>
              Loading conversations…
            </div>
          )}
          {sessions !== null && filteredSessions.length === 0 && (
            <div className="tiny muted" style={{ padding: '18px 12px', textAlign: 'center' }}>
              {search ? `No conversation matches “${search}”.` : 'No conversations yet.'}
            </div>
          )}
          {filteredSessions.map((s) => (
            <div
              key={s.id}
              className={`citem${s.id === sessionId ? ' on' : ''}`}
              role="button"
              tabIndex={0}
              onClick={() => void openSession(s.id)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') void openSession(s.id)
              }}
            >
              <div className="ci-t">{s.title ?? 'Untitled conversation'}</div>
              <div className="ci-d">
                {s.message_count} message(s) · {ago(s.last_message_at ?? s.created_at)}
              </div>
              <button
                type="button"
                className="ci-x"
                title="Delete conversation"
                onClick={(event) => {
                  event.stopPropagation()
                  void deleteSession(s.id)
                }}
              >
                <Icon name="trash" size={13} />
              </button>
            </div>
          ))}
        </div>
        {filteredSessions.length > 0 && (
          <div className="chatside-f">
            <button type="button" className="btn-link tiny" onClick={clearHistory}>
              <Icon name="trash" size={12} /> Clear all conversations
            </button>
          </div>
        )}
      </aside>

      <section className="chatmain">
        {/* Option B: the ask bar lives on top and stays there — search-first, with the
            scope chips underneath. The clock button summons the conversation history. */}
        <div className="ask-head">
          <div className="ask-row">
            <button
              type="button"
              className={`hist-btn${sideOpen ? ' on' : ''}`}
              id="sideToggle"
              title={sideOpen ? 'Hide conversations' : 'Conversation history'}
              aria-label={sideOpen ? 'Hide conversations' : 'Show conversations'}
              aria-pressed={sideOpen}
              onClick={() => setSideOpen((open) => !open)}
            >
              <Icon name="clock" size={17} />
            </button>
            {/* Only while the panel is shut. Open, the panel carries a full-width "New chat"
                button of its own, and two controls firing the same action a few pixels apart is
                clutter that makes the reader stop and work out whether they differ. Collapsed,
                this is the only way to start again without opening a panel to do it. */}
            {!sideOpen && (
              <button
                type="button"
                className="new-btn"
                onClick={startNewChat}
                disabled={isFreshChat}
                title={newChatHint}
                aria-label={newChatHint}
              >
                <Icon name="plus" size={17} />
              </button>
            )}
            <div className="ask-search">
              <Icon name="search" size={16} />
              <input
                id="chatInput"
                ref={inputRef}
                placeholder="Ask a question about policy, process or an internal app…"
                value={draft}
                disabled={kbDown}
                maxLength={MAX_QUESTION + 50}
                onChange={(event) => setDraft(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault()
                    void send(draft)
                  }
                }}
              />
              <span className="ask-kbd">Enter</span>
              <button
                type="button"
                className="ask-go"
                title="Ask"
                disabled={busy || kbDown}
                onClick={() => void send(draft)}
              >
                <Icon name="send" size={16} />
              </button>
            </div>
          </div>
          <div className="scope-row">
            <span className="scope-lbl">Scope</span>
            {scopes.map((s) => (
              <button
                key={s.value}
                type="button"
                className={`scopechip${scope === s.value ? ' on' : ''}`}
                title={s.value}
                onClick={() => void selectScope(s.value)}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>

        <div className="chat-scroll" id="chatScroll" ref={scrollRef} onScroll={updateJump}>
          {atStart ? (
            <div className="chat-empty">
              <div className="ce-head">
                <span className="ce-eyebrow">
                  {greet}, {me.given_name || me.display_name}
                </span>
                <h2>What do you need to know?</h2>
                <p className="ce-lede">
                  Type a question and get one answer with its source, down to the page.
                </p>
              </div>

              {kbDown && (
                <div className="alert a-warn" style={{ marginBottom: 0 }}>
                  <Icon name="warn" size={18} />
                  <div>
                    The knowledge base is unreachable right now, so questions can&rsquo;t be
                    answered. Try again shortly, or contact IT Helpdesk if it persists.
                  </div>
                </div>
              )}

              {areas.length > 0 && (
                <div className="ce-block">
                  <span className="ce-eyebrow">Browse by area</span>
                  <div className="area-grid">
                    {areas.map((a) => (
                      <button
                        key={a.name}
                        type="button"
                        className={`area-card${scope === a.name ? ' on' : ''}`}
                        disabled={kbDown}
                        onClick={() => browseArea(a.name)}
                      >
                        <span className="an">{a.name}</span>
                        <span className="ac">{a.hint}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {suggestions.length > 0 && (
                <div className="ce-block">
                  <span className="ce-eyebrow">Asked most</span>
                  <div className="ce-asks">
                    {suggestions.map((p) => (
                      <button
                        key={p.prompt}
                        type="button"
                        className="ce-ask"
                        disabled={kbDown}
                        onClick={() => void send(p.prompt)}
                      >
                        <span className="ca-q">{p.prompt}</span>
                        <span className="ca-c">{p.showCategory ? p.category : ''}</span>
                        <span className="ca-go">
                          <Icon name="arrowR" size={14} />
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="ce-tasks">
                <span className="ce-eyebrow">Get things done</span>
                <div className="ce-taskrow">
                  {TASKS.map((t) => (
                    <button
                      key={t.label}
                      type="button"
                      className="ce-task"
                      disabled={kbDown}
                      onClick={() => void send(t.prompt)}
                    >
                      <Icon name={t.icon} size={14} /> {t.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="sheet-col">
              {messages.map((message) => {
                if (message.role === 'user') {
                  return (
                    <div key={message.id} className="asked">
                      <span className="asked-ov">You asked</span>
                      <div className="asked-q">{message.content}</div>
                    </div>
                  )
                }

                const streaming = message.id === streamId
                const edges = edgeParas(message)
                const blocks = edges
                  ? edges.map((text) => ({ kind: 'p', text }) as AnswerBlock)
                  : parseAnswer(message.content)
                const { headline, rest } = streaming
                  ? { headline: null, rest: blocks }
                  : splitHeadline(blocks)
                const missedScope =
                  message.is_answered === false ? askScopes.current[message.id] : undefined

                return (
                  <div key={message.id} className="sheet-block">
                    <div className="sheet">
                      {streaming ? (
                        <StreamingText
                          text={(edges ?? [message.content]).join('\n\n')}
                          onDone={() => setStreamId(null)}
                        />
                      ) : (
                        <>
                        {headline && (
                          <h2 className="sheet-headline">
                            <Inline text={headline} />
                          </h2>
                        )}
                        <div className="sheet-body">
                          {message.answer_state === 'low_confidence' && (
                            <p>
                              <em>
                                Only a partial match was found, so treat this answer as
                                indicative.
                              </em>
                            </p>
                          )}
                          <AnswerBlocks
                            blocks={rest}
                            onCite={(rank) => showSource(message, rank)}
                          />
                        </div>
                        {missedScope && (
                          <div className="scoped-note">
                            Only the {missedScope} scope was searched.{' '}
                            <button
                              type="button"
                              className="btn-link"
                              onClick={() => {
                                const idx = messages.findIndex((m) => m.id === message.id)
                                const asked = [...messages.slice(0, Math.max(idx, 0))]
                                  .reverse()
                                  .find((m) => m.role === 'user')
                                if (!asked) return
                                setScope(ALL_SCOPES)
                                void send(asked.content, null)
                              }}
                            >
                              Search everything instead
                            </button>
                          </div>
                        )}
                        {message.citations.length > 0 && (
                          <div className="sheet-cites">
                            <div className="cite-row">
                              {message.citations.map((c) => (
                                <button
                                  key={`${c.document_id}-${c.rank}`}
                                  type="button"
                                  className="cite-pill"
                                  title={`${c.document_name} — page ${c.page_number}`}
                                  onClick={() => showSource(message, c.rank)}
                                >
                                  <span className="cp-i">{c.rank}</span>
                                  <span className="cp-n">{c.document_name}</span>
                                  <span className="cp-p">· p. {c.page_number}</span>
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                        </>
                      )}
                    </div>
                    {/* Outside the card, and only while the answer is hovered or focused. */}
                    {!streaming && (
                      <div className="sheet-actions">
                        <button
                          type="button"
                          className="fb-sq"
                          title="Copy answer"
                          aria-label="Copy answer"
                          onClick={() => copyMessage(message)}
                        >
                          <Icon name="copy" size={13} />
                        </button>
                        <button
                          type="button"
                          className={`fb-sq up${message.feedback === 'up' ? ' on' : ''}`}
                          title="Helpful"
                          aria-label="Helpful"
                          aria-pressed={message.feedback === 'up'}
                          onClick={() => void sendFeedback(message, 'up')}
                        >
                          <Icon name="thumbUp" size={13} />
                        </button>
                        <button
                          type="button"
                          className={`fb-sq dn${message.feedback === 'down' ? ' on' : ''}`}
                          title="Not right — report it"
                          aria-label="Not right — report it"
                          aria-pressed={message.feedback === 'down'}
                          onClick={() => openGap(message)}
                        >
                          <Icon name="thumbDn" size={13} />
                        </button>
                      </div>
                    )}
                  </div>
                )
              })}

              {/* The dots alone. Naming what is happening was reading material nobody had time
                  for, and the animation already says the only thing that matters — something is
                  in progress. The role and label stay: a screen reader gets no signal from an
                  animation, so the text it hears is the text that was doing the work. */}
              {busy && (
                <div className="thinking-line" role="status" aria-label="The assistant is answering">
                  <div className="typing">
                    <i />
                    <i />
                    <i />
                  </div>
                </div>
              )}

              {/* Follow-ups generated from the question just answered — not generic filler. */}
              {!busy &&
                !streamId &&
                lastAssistant &&
                (followups.current[lastAssistant.id] ?? []).length > 0 && (
                  <div className="related">
                    <div className="mc-h">Follow-up questions</div>
                    <div className="related-grid">
                      {(followups.current[lastAssistant.id] ?? []).map((s) => (
                        <button
                          key={s}
                          type="button"
                          className="related-card"
                          onClick={() => void send(s)}
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
            </div>
          )}
        </div>

        {!atStart && (
          <button
            type="button"
            className={`jump-latest${jumpVisible ? ' on' : ''}`}
            id="jumpLatest"
            onClick={scrollToBottom}
          >
            <Icon name="arrowD" size={14} /> Jump to latest
          </button>
        )}
      </section>

      {/* Option B: the sources column, pinned open beside the answer. */}
      {srcMessage && (
        <aside className={`srcpanel${srcOverlay ? ' open' : ''}`} aria-label="Sources">
          <div className="sp-head">
            <span className="ce-eyebrow">Where this comes from</span>
            <span className="sp-count">
              {srcMessage.citations.length} document{srcMessage.citations.length === 1 ? '' : 's'}
            </span>
            <button
              type="button"
              className="x-btn sp-x"
              onClick={() => setSrcOverlay(false)}
              aria-label="Close sources"
            >
              <Icon name="x" size={16} />
            </button>
          </div>
          <div className="sp-list">
            {srcMessage.citations.map((c) => {
              const key = `${srcMessage.id}-${c.rank}`
              return (
                <div key={key} id={`src-${key}`} className={`src-card${flashKey === key ? ' flash' : ''}`}>
                  <div className="src-top">
                    <span className="src-idx">{c.rank}</span>
                    <div style={{ minWidth: 0 }}>
                      <div className="src-name">{c.document_name}</div>
                      <div className="src-meta">
                        Page {c.page_number}
                        {c.category ? ` · ${c.category}` : ''}
                        {c.section_path ? ` · ${c.section_path}` : ''}
                      </div>
                    </div>
                  </div>
                  <div className="src-quote">
                    <HighlightedExcerpt text={c.excerpt} spans={spansOf(c)} />
                  </div>
                  <div className="src-acts">
                    {/* Plain links, because a bare GET attaches no identity — download_url
                        carries a signed token for this one document, which is the whole reason
                        the token exists. The server hands back a link only when it has
                        something to serve: null means neither the original file nor a reachable
                        source address, and a button that ends in "file wasn't available on
                        site" is worse than a sentence saying so. `download` is honoured only
                        same-origin, so it is offered for our own bytes and not for a link that
                        points out at the source system. */}
                    {c.download_url ? (
                      <>
                        <a
                          className="src-act"
                          href={c.download_url}
                          target="_blank"
                          rel="noreferrer"
                        >
                          Open at page {c.page_number}
                        </a>
                        {c.download_url.startsWith('/api/') && (
                          <a className="src-act dim" href={c.download_url} download>
                            Download
                          </a>
                        )}
                      </>
                    ) : (
                      <span className="src-gone">Original file not held on this server</span>
                    )}
                    {!c.source_available && (
                      <span className="src-gone">Source moved or deleted since indexing</span>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </aside>
      )}
    </div>
  )
}
